import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { useRepoReplicache } from './use-repo-replicache'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { type Client } from '@basehub/data/replicache/index'
import { useMemo } from 'react'
import { useUserData } from './use-user-data'
import { makeShortNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import type { CollaborationUser } from '~/lib/tiptap/extensions/collaboration'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'

/**
 * Get all clients active in this space
 */
export const useRepoPresence = () => {
  const { data: repoReplicache } = useRepoReplicache()
  const optimisticUser = useUserData(null).data
  const replicache = repoReplicache?.replicache
  const { branch } = useSelectedBranch()

  const { data: clients } = useReplicacheQueryV2({
    key: ['users', optimisticUser?.id, repoReplicache?.repoId, branch?.id],
    replicache,
    fetcher: async (tx) => {
      return await repoSpaceMutators.listActiveClients(tx, {
        now: Date.now(),
        branchId: branch?.id ?? '',
      })
    },
    enabled: !!branch,
  })

  const { others, me } = useMemo<{ others?: Client[]; me?: Client }>(
    () =>
      clients?.reduce(
        (acc: { others: Client[]; me?: Client }, c) => {
          if (c.userId === optimisticUser?.id) {
            acc.me = c
          } else {
            acc.others?.push(c)
          }
          return acc
        },
        { others: [] }
      ) ?? {},
    [clients, optimisticUser?.id]
  )

  return { clients, others, me }
}

export const useBlockAwareness = (blockId: string) => {
  const { others } = useRepoPresence()
  const list = useMemo(
    () =>
      others?.reduce((acc: { value: string; blockId: string }[], client) => {
        if (
          client.selection?.blockId === blockId &&
          client.selection.awareness
        ) {
          acc.push({ blockId, value: client.selection.awareness })
        }
        return acc
      }, []),
    [blockId, others]
  )

  return list
}

export function useCollaborationData(): CollaborationUser | null {
  const user = useUserData(null).data
  const { me } = useRepoPresence()

  if (!user || !me) {
    return null
  }

  return {
    name: makeShortNameFromClerkUser(user),
    color: me?.color ?? '#FF511C',
    id: user.id,
  }
}
